import { Field, Label, Switch } from "@headlessui/react";
import { cva, type VariantProps } from "class-variance-authority";
import { type FC, type ReactNode, type Ref } from "react";
import { cn } from "../lib/utils";
import { trackingAttrs } from "../utils";

const colorClasses = {
	primary: "",
	confirm: "",
};

export type ToggleColor = keyof typeof colorClasses;
export const TOGGLE_COLORS = Object.keys(colorClasses) as readonly ToggleColor[];

const trackVariants = cva(
	"group relative inline-flex items-center shrink-0 rounded-full border-2 border-transparent bg-bc-strong transition-colors duration-200 ease-in-out cursor-pointer data-[disabled]:cursor-default data-[disabled]:opacity-60",
	{
		variants: {
			color: colorClasses,
			size: {
				sm: "h-5 w-9",
				md: "h-6 w-11",
			},
		},
		compoundVariants: [
			{ color: "primary", className: "data-[checked]:bg-brand-accent" },
			{ color: "confirm", className: "data-[checked]:bg-confirm" },
		],
		defaultVariants: {
			color: "primary",
			size: "md",
		},
	},
);

const knobVariants = cva(
	"pointer-events-none inline-block transform rounded-full bg-surface-base shadow-lg ring-0 transition-transform duration-200 ease-in-out translate-x-0",
	{
		variants: {
			size: {
				sm: "h-4 w-4 group-data-[checked]:translate-x-4",
				md: "h-5 w-5 group-data-[checked]:translate-x-5",
			},
		},
		defaultVariants: {
			size: "md",
		},
	},
);

type TrackVariantProps = VariantProps<typeof trackVariants>;

export type ToggleProps = {
	/** Current on/off state. */
	checked: boolean;

	/** Fired with the next state when the user flips the toggle. */
	onChange?: (checked: boolean) => void;

	/**
	 * Semantic intent driving the *on*-track color via the CVA color matrix.
	 * - `primary` — `bg-brand-accent` when on (the #2563EB action blue, matches `Button`)
	 * - `confirm` — `bg-confirm` when on
	 * @default "primary"
	 */
	color?: ToggleColor;

	/**
	 * Track/knob dimensions.
	 * - `sm` — 20×36px track, 16px knob
	 * - `md` — 24×44px track, 20px knob (matches the legacy `ui/Slider`)
	 * @default "md"
	 */
	size?: TrackVariantProps["size"];

	/** Disables interaction and dims the control. */
	disabled?: boolean;

	/**
	 * Optional label rendered beside the track. When provided, the track and label are
	 * wrapped in a Headless `Field` so clicking the label also flips the toggle. Compute
	 * state-dependent content (icon, color, copy) in the consumer — the primitive owns no
	 * domain styling.
	 */
	label?: ReactNode;

	/** Accessible name. Falls back to the `label` association when omitted. */
	"aria-label"?: string;

	/** Forwarded to the underlying `<button role="switch">`. */
	"data-testid"?: string;

	/** Analytics identifier — expands to `data-pendo-id` + `data-amp-track-id`. */
	trackingId?: string;

	className?: string;

	ref?: Ref<HTMLButtonElement>;
};

export const Toggle: FC<ToggleProps> = ({
	checked,
	onChange,
	color,
	size,
	disabled,
	label,
	trackingId,
	className,
	ref,
	"aria-label": ariaLabel,
	"data-testid": testId,
}) => {
	const track = (
		<Switch
			ref={ref}
			checked={checked}
			onChange={onChange}
			disabled={disabled}
			aria-label={ariaLabel}
			data-testid={testId}
			className={cn(trackVariants({ color, size }), className)}
			{...trackingAttrs(trackingId)}
		>
			<span aria-hidden="true" className={knobVariants({ size })} />
		</Switch>
	);

	if (label === undefined) return track;

	return (
		<Field className="inline-flex items-center gap-x-[6px]">
			{track}
			<Label className={cn("select-none", disabled ? "cursor-default" : "cursor-pointer")}>
				{label}
			</Label>
		</Field>
	);
};
Toggle.displayName = "Toggle";
